/**
 * CMS Footer Queries
 *
 * Server-side query for fetching footer configuration by tenant + usage role.
 * Footers are selected by their `usage` role ('website' | 'landingPages'), which is
 * unique per tenant (enforced in the CMS) — never by hardcoded document ID.
 */

import { query } from "@solidjs/router";
import type { CMSFooter, CMSFooterResponse } from "~/types/cms";
import { env } from "~/config/env";
import { buildFootersListUrl, type CmsUsage } from "../queryUrls";
import { logger } from "~/lib/logger/logger";
import { fetchFromCMS } from "../queryUtils";
import { evictIfFull, touchEntry } from "~/lib/cache/cappedMap";

/** 60s cache — footer content rarely changes */
const CACHE_TTL_MS = 60_000;
const MAX_CACHE_SIZE = 20;
const footerByUsageCache = new Map<string, { data: CMSFooter | null; expiresAt: number }>();

/** Clear the server-side footer Map cache. Called by webhook invalidation. */
export function clearFooterCache(tenantSlug?: string, usage?: CmsUsage) {
  if (tenantSlug && usage) {
    footerByUsageCache.delete(`${tenantSlug}:${usage}`);
  } else {
    footerByUsageCache.clear();
  }
}

/**
 * Server-side query for fetching a footer by tenant + usage role.
 *
 * @param tenantSlug - The tenant slug (e.g., 'absolut-radio')
 * @param usage - The surface role ('website' | 'landingPages')
 * @returns Footer configuration with navigation and layout blocks, or null if not found
 */
export const getFooterByUsageQuery = query(async (tenantSlug: string, usage: CmsUsage) => {
  "use server";

  const LOG_PREFIX = "[footerByUsage]";
  const cacheKey = `${tenantSlug}:${usage}`;
  const cached = footerByUsageCache.get(cacheKey);
  if (cached && Date.now() < cached.expiresAt) {
    touchEntry(footerByUsageCache, cacheKey);
    return cached.data;
  }

  const url = `${env.cmsApiUrl}${buildFootersListUrl(tenantSlug, usage)}`;

  const data = await fetchFromCMS<CMSFooterResponse>(url, { logPrefix: LOG_PREFIX });

  const footer = data?.docs?.[0] ?? null;

  if (!footer) {
    logger.warn(`${LOG_PREFIX} No '${usage}' footer found for tenant: ${tenantSlug}`);
    return null;
  }

  logger.info(`${LOG_PREFIX} Found footer: ${footer.name}`);
  evictIfFull(footerByUsageCache, MAX_CACHE_SIZE);
  footerByUsageCache.set(cacheKey, {
    data: footer as CMSFooter,
    expiresAt: Date.now() + CACHE_TTL_MS,
  });
  return footer as CMSFooter;
}, "footerByUsage");
